import React, { useCallback } from 'react';
import { selectCustomerConfigurations } from 'redux/root.selectors';
import { useSelector } from 'react-redux';

import { Themes, ThemeSettings } from 'redux/configuration/configuration';
import { initialStyles, defaultStyles, lightThemeBackground, darkThemeBackground } from './useTheme.constants';

const getHslColorValue = (colorSettingDto: Record<string, any>, lightnessDiff: number | null = null) =>
  colorSettingDto
    ? `hsl(${colorSettingDto.hue}, ${colorSettingDto.saturation}%, ${getLightnessValue(lightnessDiff as number, colorSettingDto.lightness)}%)`
    : 'hsl(0, 0%, 100%)';

const getHslOpaqueColorValue = (colorSettingDto: Record<string, any>) =>
  colorSettingDto
    ? `hsla(${colorSettingDto.hue}, ${colorSettingDto.saturation}%, ${colorSettingDto.lightness}%, 0.8)`
    : 'hsla(0, 0%, 100%, 1)';

const getLightnessValue = (lightnessDiff: number, originalLightness: number) =>
  !lightnessDiff
    ? originalLightness
    : originalLightness + lightnessDiff > 97
      ? 97
      : originalLightness + lightnessDiff < 8
        ? 8
        : originalLightness + lightnessDiff;

const useIsDarkTheme = () => {
  const customerConfiguration = useSelector(selectCustomerConfigurations);

  // We also check the url query param "theme"
  // 1) theme=light -> use light theme
  // 2) theme=dark -> use dark theme
  const urlParams = new URLSearchParams(window.location.search);
  const themeParam = urlParams.get('theme');

  if (themeParam === 'light') {
    return false;
  }

  // In cases where
  // 1) theme=dark or
  // 2) no "theme" query param is provided
  // we use dark theme (if configured)
  const darkTheme = customerConfiguration?.themes?.dark as ThemeSettings;
  return darkTheme != null;
};

const useSetCustomTheme = (setStyles: React.SetStateAction<React.Dispatch<Record<string, any>>>) => {
  const isDarkTheme = useIsDarkTheme();

  const setCustomTheme = useCallback((themes: Themes) => {
    const defaultTheme = themes.default as ThemeSettings;
    const darkTheme = themes.dark as ThemeSettings;

    const theme = isDarkTheme
      ? darkTheme
      : defaultTheme;

    // Backgrounds are statically set based on the theme
    // We do not allow user to change backgrounds dynamically
    const backgrounds = isDarkTheme
      ? darkThemeBackground
      : lightThemeBackground;

    const styles = {
      colorHeading: getHslColorValue(theme.headingColor),
      colorParagraph: getHslColorValue(theme.paragraphColor),

      colorPrimaryButtonBackground: getHslColorValue(theme.primaryButtonBackgroundColor),
      colorPrimaryButtonBackgroundOpaque: getHslOpaqueColorValue(theme.primaryButtonBackgroundColor),
      colorPrimaryButtonBackgroundLight: getHslColorValue(theme.primaryButtonBackgroundColor, 30),
      colorPrimaryButtonBackgroundDark: getHslColorValue(theme.primaryButtonBackgroundColor, -10),
      colorPrimaryButtonText: getHslColorValue(theme.primaryButtonTextColor),

      colorSecondaryButtonBackground: getHslColorValue(theme.secondaryButtonBackgroundColor),
      colorSecondaryButtonBackgroundLight: getHslColorValue(theme.secondaryButtonBackgroundColor, 30),
      colorSecondaryButtonBackgroundDark: getHslColorValue(theme.secondaryButtonBackgroundColor, -10),
      colorSecondaryButtonText: getHslColorValue(theme.secondaryButtonTextColor),

      colorSuccess: getHslColorValue(theme.successColor),
      colorError: getHslColorValue(theme.errorColor),
      colorWarning: getHslColorValue(theme.warningColor),

      colorGreenPrimary: '#00BE4A',
      colorGreenSecondary: '#A4DAC3',
      colorGreenLight: '#D1F0E1',

      logoVisibility: 'visible',
    };

    setStyles((prevStyles) => ({
      ...prevStyles,
      ...styles,
      ...backgrounds
    }));
  }, [isDarkTheme, setStyles]);

  return setCustomTheme;
};

export {
  useSetCustomTheme,
  getHslColorValue,
  getLightnessValue,
  initialStyles,
  defaultStyles,
  lightThemeBackground,
  darkThemeBackground,
  useIsDarkTheme
};
